/**
 * Theme switching — light (default, white + cyan) ⇄ dark (graphite + cyan).
 * The chosen theme persists in localStorage and applies via
 * html[data-theme='dark']; index.html sets it pre-paint to avoid a flash.
 * A 'helix:theme' window event lets imperative canvases (HeroCanvas) repaint.
 */
export type Theme = 'light' | 'dark';

const KEY = 'helix-theme';

export function getTheme(): Theme {
  // SSR (§9.6 prerender): no document → the default. Dark-theme visitors get a
  // one-frame icon correction on hydration; the page itself is unaffected
  // (dark styling hangs off html[data-theme], set pre-paint by index.html).
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

export function setTheme(theme: Theme): void {
  if (theme === 'dark') document.documentElement.dataset.theme = 'dark';
  else delete document.documentElement.dataset.theme;
  try { localStorage.setItem(KEY, theme); } catch { /* private mode */ }
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', theme === 'dark' ? '#0b0d12' : '#ffffff');
  window.dispatchEvent(new Event('helix:theme'));
}

export function toggleTheme(): Theme {
  const next: Theme = getTheme() === 'dark' ? 'light' : 'dark';
  setTheme(next);
  return next;
}
